import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CalendarBlank, MapPin, ArrowRight, Play, HandHeart, PlayCircle, Heart, PhoneCall, EnvelopeSimple, ArrowUpRight, Quotes, Fire, Crown } from "@phosphor-icons/react";
import { CHURCH, MEDIA, MINISTRIES, FEATURED, GALLERY_STRIP, SCRIPTURE } from "@/lib/church";
import { api } from "@/lib/api";
import OlivePocket from "@/components/OlivePocket";
import FallRally from "@/components/FallRally";

export default function Home() {
    const [events, setEvents] = useState([]);
    useEffect(() => {
        api.get("/events").then((r) => {
            const today = new Date().toISOString().slice(0, 10);
            const upcoming = (r.data || [])
                .filter((e) => e.date >= today)
                .sort((a, b) => a.date.localeCompare(b.date))
                .slice(0, 4);
            setEvents(upcoming);
        }).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page" className="bg-white">
            {/* HERO */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#4C1D95]">
                <div className="absolute inset-0">
                    <img src={MEDIA.hero} alt="GMOAMC worship" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4C1D95]/95 via-[#4C1D95]/70 to-[#4C1D95]/25" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/70 via-transparent to-transparent" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24 lg:py-20">
                    <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white mb-8" data-testid="hero-eyebrow">
                        <span className="w-1.5 h-1.5 bg-[#FFC72C] rounded-full" /> West Philadelphia · Since the AME tradition of 1816
                    </div>

                    <h1 className="font-display font-black tracking-tighter text-white text-5xl sm:text-6xl lg:text-8xl xl:text-[7rem] leading-[0.92] max-w-5xl animate-fade-up animate-delay-100" data-testid="hero-title">
                        A church family<br />
                        rooted in <span className="text-[#FFC72C]">faith</span>.<br />
                        Open to all.
                    </h1>

                    <p className="mt-8 text-lg sm:text-xl text-white/85 max-w-2xl animate-fade-up animate-delay-200 leading-relaxed" data-testid="hero-subtitle">
                        Greater Mount Olive AME Church — a Spirit-filled community in West Philadelphia under the leadership of <span className="text-[#FFC72C] font-semibold">{CHURCH.pastor}</span>
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3 animate-fade-up animate-delay-300">
                        <Link to="/contact#visit" data-testid="hero-cta-visit" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-7 py-4 text-xs font-bold uppercase tracking-widest transition-colors">
                            Plan Your Visit <ArrowRight size={14} weight="bold" />
                        </Link>
                        <Link to="/give#stewards-details" data-testid="hero-cta-stewards" className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-[#2E1A47] px-7 py-4 text-xs font-bold uppercase tracking-widest transition-colors">
                            <Crown size={14} weight="fill" className="text-[#FFC72C]" /> Steward&rsquo;s Advantage
                        </Link>
                        <a href={CHURCH.giveUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-give" className="inline-flex items-center gap-3 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-10 py-5 text-base font-black uppercase tracking-widest transition-colors shadow-xl">
                            <HandHeart size={20} weight="fill" /> Give Online Now <ArrowUpRight size={18} weight="bold" />
                        </a>
                    </div>
                </div>

                {/* Refined service times bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-[#2E1A47]/80 backdrop-blur-md border-t border-white/10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-white">
                        {CHURCH.services.map((s) => (
                            <div key={s.label} className="flex items-baseline justify-between md:justify-start gap-4">
                                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">{s.label}</span>
                                <span className="text-base font-bold text-[#FFC72C]">{s.time}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FALL RALLY DOLLAR-THON */}
            <FallRally idPrefix="fall-rally" />

            {/* GIVE CTA — moved to top for visibility */}
            <section className="relative overflow-hidden" data-testid="home-give-cta">
                <div className="absolute inset-0">
                    <img src={MEDIA.congregation} alt="Congregation" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#4C1D95]/93" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="overline text-[#FFC72C] mb-4">Generosity in Action</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter leading-tight text-white mb-6">
                            Give cheerfully.<br />Change lives.
                        </h2>
                        <p className="text-base lg:text-lg text-white/90 max-w-xl leading-relaxed mb-8">Your generosity fuels worship, youth, outreach, and the daily ministry of our church. Thank you for partnering with us.</p>
                        <div className="flex flex-wrap gap-4">
                            <a href={CHURCH.giveUrl} target="_blank" rel="noopener noreferrer" data-testid="home-give-online-btn" className="inline-flex items-center gap-3 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-10 py-5 text-base font-black uppercase tracking-widest transition-colors shadow-xl">
                                <HandHeart size={20} weight="fill" /> Give Online Now <ArrowUpRight size={18} weight="bold" />
                            </a>
                            <Link to="/give" data-testid="home-give-learn-btn" className="inline-flex items-center gap-3 bg-white hover:bg-[#FFC72C] text-[#2E1A47] px-10 py-5 text-base font-black uppercase tracking-widest transition-colors shadow-xl ring-2 ring-[#FFC72C]">
                                Steward&rsquo;s Advantage <ArrowRight size={18} weight="bold" />
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-white">
                        <div className="bg-white/10 backdrop-blur-sm p-7 border border-white/15">
                            <p className="font-display text-4xl lg:text-5xl font-bold text-[#FFC72C] mb-2 tracking-tight">100%</p>
                            <p className="text-[10px] uppercase tracking-widest font-bold">Secure giving via Qgiv</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm p-7 border border-white/15">
                            <p className="font-display text-4xl lg:text-5xl font-bold text-[#FFC72C] mb-2 tracking-tight">24/7</p>
                            <p className="text-[10px] uppercase tracking-widest font-bold">Anytime, anywhere</p>
                        </div>
                        <div className="bg-[#FFC72C] text-[#2E1A47] p-7 col-span-2">
                            <Fire size={28} weight="duotone" className="mb-3" />
                            <p className="font-display text-xl font-bold tracking-tight">Tithes · Offerings · Missions · Building Fund</p>
                            <p className="text-sm text-[#2E1A47]/80 mt-1">One-time or recurring gifts. Tax-deductible receipts available.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS BAR */}
            <section className="bg-[#2E1A47] text-white py-14 lg:py-16" data-testid="stats-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {CHURCH.stats.map((s, i) => (
                        <div key={s.label} className={`${i > 0 ? 'md:border-l border-white/15 md:pl-8' : ''}`} data-testid={`stat-${i}`}>
                            <p className="font-display text-4xl lg:text-5xl font-black text-[#FFC72C] tracking-tighter leading-none">{s.number}</p>
                            <p className="mt-3 text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SCRIPTURE BANNER — photo-based, refined */}
            <section className="relative overflow-hidden" data-testid="scripture-section">
                <div className="absolute inset-0">
                    <img src={MEDIA.african} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#2E1A47]/80" />
                </div>
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center text-white">
                    <Quotes size={48} weight="fill" className="text-[#FFC72C] mx-auto mb-8 opacity-90" />
                    <p className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight leading-[1.2] text-white italic">
                        {SCRIPTURE.text}
                    </p>
                    <div className="mt-10 inline-flex items-center gap-3 text-[#FFC72C]">
                        <span className="h-px w-12 bg-[#FFC72C]" />
                        <span className="text-xs uppercase tracking-[0.3em] font-bold">{SCRIPTURE.ref}</span>
                        <span className="h-px w-12 bg-[#FFC72C]" />
                    </div>
                </div>
            </section>

            {/* FEATURED CARDS */}
            <section className="bg-white py-20 lg:py-28" data-testid="featured-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <p className="overline text-[#4C1D95] mb-3">What's Happening</p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#2E1A47] tracking-tighter leading-tight max-w-3xl">
                                The latest at Greater Mount Olive.
                            </h2>
                        </div>
                        <Link to="/events" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#4C1D95] hover:gap-3 transition-all">
                            View All <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {FEATURED.map((f, i) => (
                            <Link to={f.href} key={f.id} data-testid={`featured-card-${f.id}`} className={`group block relative aspect-[3/4] overflow-hidden zoom-on-hover animate-fade-up animate-delay-${(i + 1) * 100}`} style={f.bgColor ? { backgroundColor: f.bgColor } : undefined}>
                                <img
                                    src={f.image}
                                    alt={f.title}
                                    className={`absolute inset-0 w-full h-full ${f.contain ? 'object-contain p-5' : 'object-cover'}`}
                                    style={f.rotate ? { transform: `rotate(${f.rotate}deg) scale(1.8)`, transformOrigin: "center" } : undefined}
                                />
                                <div className={`absolute inset-0 ${f.contain ? 'bg-gradient-to-t from-[#2E1A47] via-[#2E1A47]/30 via-60% to-transparent' : 'bg-gradient-to-t from-[#2E1A47] via-[#2E1A47]/40 to-transparent'}`} />
                                <div className="absolute inset-x-0 bottom-0 p-6">
                                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC72C] mb-3">{f.eyebrow}</p>
                                    <h3 className="font-display text-xl lg:text-2xl font-bold text-white tracking-tight leading-snug mb-2">{f.titleNode || f.title}</h3>
                                    <p className="text-xs text-white/80 mb-4">{f.subtitle}</p>
                                    <span className="inline-flex items-center gap-2 text-[10px] uppercase font-bold tracking-widest text-white group-hover:text-[#FFC72C] transition-colors">
                                        {f.cta} <ArrowUpRight size={12} weight="bold" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* PASTOR WELCOME */}
            <section className="bg-[#F4F7F9] py-20 lg:py-28" data-testid="pastor-welcome">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-5 relative">
                        <div className="zoom-on-hover">
                            <img src={MEDIA.pastorReading} alt={CHURCH.pastor} className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="absolute -bottom-4 -right-4 bg-[#FFC72C] text-[#2E1A47] px-6 py-4 hidden sm:block">
                            <p className="font-display text-lg font-bold leading-tight">{CHURCH.pastor}</p>
                            <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Senior Pastor</p>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="overline text-[#4C1D95] mb-4">A Word from Our Pastor</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-[#2E1A47] leading-[1.0] mb-8">
                            Whoever you are, wherever you've been — you belong here.
                        </h2>
                        <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
                            <p>Greater Mount Olive is more than a church — it's a family. Whether you're returning to faith, exploring it for the first time, or seeking a community that loves boldly, we have a seat for you.</p>
                            <p>Come worship with us. Be encouraged. Be challenged. Be loved.</p>
                        </div>
                        <Link to="/pastor" data-testid="meet-pastor-link" className="inline-flex items-center gap-2 mt-8 bg-[#4C1D95] hover:bg-[#3B0F75] text-white px-7 py-4 text-xs font-bold uppercase tracking-widest transition-colors">
                            Meet {CHURCH.pastor} <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PHOTO STRIP */}
            <section className="bg-[#2E1A47] py-8 overflow-hidden" data-testid="photo-strip">
                <div className="flex gap-3 animate-marquee whitespace-nowrap" style={{ animationDuration: "35s" }}>
                    {[...GALLERY_STRIP, ...GALLERY_STRIP].map((src, i) => (
                        <div key={i} className="inline-block w-40 lg:w-48 aspect-[4/3] shrink-0 overflow-hidden">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                        </div>
                    ))}
                </div>
            </section>

            {/* MINISTRIES */}
            <section className="bg-white py-20 lg:py-28" data-testid="ministries-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <p className="overline text-[#4C1D95] mb-3">Get Connected</p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-[#2E1A47] leading-tight max-w-3xl">
                                Ministries serving our community.
                            </h2>
                        </div>
                        <Link to="/ministries" data-testid="ministries-explore-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#4C1D95] hover:gap-3 transition-all">
                            Explore All <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        <Link to="/ministries" data-testid="ministry-tile-youth" className="md:col-span-8 md:row-span-2 group relative aspect-[16/10] md:aspect-auto md:min-h-[560px] overflow-hidden zoom-on-hover">
                            <img src={MINISTRIES[0].image} alt={MINISTRIES[0].name} className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/95 via-[#2E1A47]/30 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10">
                                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC72C] mb-3">{MINISTRIES[0].tagline}</p>
                                <h3 className="font-display text-3xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-3">
                                    {MINISTRIES[0].id === "house2house" ? (
                                        <>House<span className="text-[#FFC72C] font-black text-[1.3em] align-middle">²</span>House Ministries</>
                                    ) : MINISTRIES[0].name}
                                </h3>
                                <p className="text-base text-white/85 max-w-lg leading-relaxed">{MINISTRIES[0].description}</p>
                            </div>
                        </Link>
                        {[MINISTRIES[1], MINISTRIES[2]].map((m) => (
                            <Link to="/ministries" key={m.id} data-testid={`ministry-tile-${m.id}`} className="md:col-span-4 group relative aspect-[5/4] overflow-hidden zoom-on-hover">
                                <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/95 via-[#2E1A47]/25 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-6">
                                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC72C] mb-2">{m.tagline}</p>
                                    <h3 className="font-display text-2xl font-bold text-white tracking-tight leading-tight">
                                    {m.id === "house2house" ? (
                                        <>House<span className="text-[#FFC72C] font-black text-[1.3em] align-middle">²</span>House Ministries</>
                                    ) : m.name}
                                </h3>
                                </div>
                            </Link>
                        ))}
                        {MINISTRIES.slice(3, 7).map((m) => (
                            <Link to="/ministries" key={m.id} data-testid={`ministry-tile-${m.id}`} className="md:col-span-3 group relative aspect-square overflow-hidden zoom-on-hover">
                                <img
                                    src={m.image}
                                    alt={m.name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={m.rotate ? { transform: `rotate(${m.rotate}deg) scale(1.8)`, transformOrigin: "center" } : undefined}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/95 via-[#2E1A47]/30 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-5">
                                    <h3 className="font-display text-lg lg:text-xl font-bold text-white tracking-tight leading-tight">{m.name}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRAYER SECTION */}
            <section className="bg-[#4C1D95] py-20 lg:py-24 text-white" data-testid="prayer-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <p className="overline text-[#FFC72C] mb-4">Need Prayer?</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter leading-tight mb-6">
                            We will pray with you.
                        </h2>
                        <p className="text-base lg:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">Join us nightly, request prayer, or connect with our bereavement ministry. You are never alone.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white text-[#2E1A47] p-8 lg:p-10 flex flex-col" data-testid="prayer-card-daily">
                            <PhoneCall size={32} weight="duotone" className="text-[#4C1D95] mb-5" />
                            <p className="overline text-[#4C1D95] mb-2">{CHURCH.prayer.dailyTime}</p>
                            <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-4">Pray with us.</h3>
                            <div className="text-slate-700 leading-relaxed mb-5 text-sm space-y-3">
                                <div className="border-l-2 border-[#FFC72C] pl-3">
                                    <p>Dial: <a href={`tel:${CHURCH.prayer.dailyDialIn.replace(/[^0-9+]/g, "")}`} className="font-bold text-[#2E1A47] hover:text-[#4C1D95]">{CHURCH.prayer.dailyDialIn}</a></p>
                                    <p>Code: <span className="font-bold text-[#2E1A47]">{CHURCH.prayer.dailyCode}</span></p>
                                </div>
                                {CHURCH.prayer.callLines.map((line) => (
                                    <div key={line.day} className="border-l-2 border-[#FFC72C] pl-3">
                                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#4C1D95]">{line.day} · {line.time}</p>
                                        <p>Dial: <a href={`tel:${line.dialIn.replace(/[^0-9+]/g, "")}`} className="font-bold text-[#2E1A47] hover:text-[#4C1D95]">{line.dialIn}</a></p>
                                        <p>Code: <span className="font-bold text-[#2E1A47]">{line.code}</span></p>
                                    </div>
                                ))}
                            </div>
                            <a href={`tel:${CHURCH.prayer.dailyDialIn.replace(/[^0-9+]/g, "")}`} className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#4C1D95] hover:gap-3 transition-all" data-testid="call-prayer-btn">
                                Call Prayer Line <ArrowRight size={14} weight="bold" />
                            </a>
                        </div>
                        <div className="bg-[#FFC72C] text-[#2E1A47] p-8 lg:p-10 flex flex-col" data-testid="prayer-card-request">
                            <Heart size={32} weight="duotone" className="text-[#2E1A47] mb-5" />
                            <p className="overline mb-2">Prayer Request</p>
                            <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-4">We're listening.</h3>
                            <p className="text-[#2E1A47]/80 leading-relaxed mb-5 text-sm">Send us your request — a minister will personally reach out to you.</p>
                            <Link to="/contact" data-testid="prayer-request-button" className="mt-auto inline-flex items-center gap-2 bg-[#2E1A47] hover:bg-[#4C1D95] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors w-fit">
                                Submit Request <ArrowRight size={14} weight="bold" />
                            </Link>
                        </div>
                        <div className="bg-white text-[#2E1A47] p-8 lg:p-10 flex flex-col" data-testid="prayer-card-bereavement">
                            <EnvelopeSimple size={32} weight="duotone" className="text-[#4C1D95] mb-5" />
                            <p className="overline text-[#4C1D95] mb-2">Healing Love</p>
                            <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-4">Bereavement Ministry.</h3>
                            <p className="text-slate-600 leading-relaxed mb-5 text-sm">Lost a loved one? Our Healing Love Ministry walks with you through grief.</p>
                            <a href={CHURCH.emailHref} className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#4C1D95] hover:gap-3 transition-all" data-testid="bereavement-btn">
                                Connect With Us <ArrowRight size={14} weight="bold" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT TO EXPECT */}
            <section className="bg-white py-20 lg:py-28" data-testid="what-to-expect">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-12">
                        <p className="overline text-[#4C1D95] mb-3">Plan Your Visit</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#2E1A47] tracking-tighter leading-tight">
                            What to expect on Sunday.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-7 bg-[#2E1A47] text-white p-10 lg:p-14">
                            <p className="overline text-[#FFC72C] mb-6">Service Times</p>
                            <ul className="space-y-5">
                                {CHURCH.services.map((s) => (
                                    <li key={s.label} className="flex justify-between items-baseline border-b border-white/10 pb-5">
                                        <span className="font-display text-xl lg:text-2xl font-bold tracking-tight">{s.label}</span>
                                        <span className="text-[#FFC72C] text-2xl lg:text-3xl font-bold tracking-tight">{s.time}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="md:col-span-5 relative aspect-square md:aspect-auto md:min-h-full overflow-hidden zoom-on-hover">
                            <img src={MEDIA.african} alt="Worship at GMOAMC" className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/90 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-8">
                                <p className="overline text-[#FFC72C] mb-2">Doors Open at 9:30</p>
                                <p className="font-display text-2xl lg:text-3xl font-bold text-white tracking-tight leading-tight">Come early. Stay late.</p>
                            </div>
                        </div>
                        <div className="md:col-span-4 bg-[#FFC72C] text-[#2E1A47] p-8">
                            <p className="overline mb-3">For Families</p>
                            <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight mb-2">Kids welcomed.</h3>
                            <p className="text-[#2E1A47]/80 leading-relaxed text-sm">Sunday School at 10:00 AM. Children's church during morning worship.</p>
                        </div>
                        <div className="md:col-span-4 bg-[#F4F7F9] p-8">
                            <p className="overline text-[#4C1D95] mb-3">What to Wear</p>
                            <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight text-[#2E1A47] mb-2">Come as you are.</h3>
                            <p className="text-slate-600 leading-relaxed text-sm">Sunday best or casual — what matters is you came.</p>
                        </div>
                        <div className="md:col-span-4 bg-[#4C1D95] text-white p-8">
                            <p className="overline text-[#FFC72C] mb-3">First-time</p>
                            <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight mb-2">We'd love to meet you.</h3>
                            <p className="text-white/85 leading-relaxed text-sm">Stop by our welcome table for a small gift and friendly hello.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* UPCOMING EVENTS */}
            <section className="bg-[#F4F7F9] py-20 lg:py-28" data-testid="upcoming-events">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div>
                            <p className="overline text-[#4C1D95] mb-3">What's Coming Up</p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#2E1A47] tracking-tighter leading-tight">Upcoming events.</h2>
                        </div>
                        <Link to="/events" data-testid="view-all-events-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#4C1D95] hover:gap-3 transition-all">
                            View All <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {events.map((ev) => {
                            const d = new Date(ev.date + "T12:00:00Z");
                            return (
                                <div key={ev.id} className="bg-white p-8 hover:shadow-xl transition-all duration-300 flex flex-col" data-testid={`event-card-${ev.id}`}>
                                    <div className="flex items-baseline gap-5 mb-6 pb-6 border-b border-slate-100">
                                        <div className="text-center">
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4C1D95] font-bold">{d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })} · {d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}</p>
                                            <p className="font-display text-5xl font-black text-[#2E1A47] leading-none tracking-tighter">{d.getUTCDate()}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">{ev.time}</p>
                                            <p className="text-xs text-slate-500">{ev.location}</p>
                                        </div>
                                    </div>
                                    <h3 className="font-display text-xl lg:text-2xl font-bold text-[#2E1A47] mb-3 leading-snug tracking-tight">{ev.title}</h3>
                                    <p className="text-slate-600 leading-relaxed text-sm line-clamp-3 mb-5">{ev.description}</p>
                                    {ev.cta && (
                                        <Link
                                            to={ev.cta_href || "/contact"}
                                            data-testid={`event-cta-${ev.id}`}
                                            className="mt-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#4C1D95] hover:text-[#2E1A47] hover:gap-3 transition-all"
                                        >
                                            {ev.cta} <ArrowRight size={12} weight="bold" />
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FRIENDS REGISTRY CALLOUT */}
            <section className="bg-[#1a0f2e] text-white py-24 lg:py-32 relative overflow-hidden" data-testid="home-friends-callout">
                <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-[#4C1D95]/40 blur-3xl rounded-full" />
                <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-[#FFC72C]/10 blur-3xl rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-12 h-px bg-[#FFC72C]" />
                            <p className="overline text-[#FFC72C]">A Registry of Stewardship</p>
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.9] mb-8">
                            Friends of <span className="italic text-[#FFC72C]">Greater Mount Olive</span>.
                        </h2>
                        <p className="text-lg lg:text-xl text-white/75 leading-relaxed max-w-3xl mb-10">
                            Sustaining over a <span className="text-[#FFC72C] font-medium">200-year legacy</span> of worship, service, and community leadership. For alumni, partners, and stakeholders who wish to stand with the enduring mission of the AME church.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/friends" data-testid="home-friends-explore-btn" className="inline-flex items-center gap-3 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors">
                                Explore the Registry <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link to="/friends#stewardship" data-testid="home-friends-commitment-btn" className="inline-flex items-center gap-3 border border-white/25 hover:border-[#FFC72C] hover:text-[#FFC72C] text-white px-10 py-5 text-xs font-black uppercase tracking-[0.2em] transition-colors">
                                Our Commitment
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 lg:p-10">
                            <p className="overline text-[#FFC72C] mb-6">Three Ways to Stand</p>
                            <ul className="space-y-5">
                                {[
                                    { tier: "I", name: "Sustaining Friend", note: "Monthly Partnership" },
                                    { tier: "II", name: "Legacy Friend", note: "Annual Patronage" },
                                    { tier: "III", name: "Cornerstone Friend", note: "Major & Planned Giving" },
                                ].map((r) => (
                                    <li key={r.tier} className="flex items-start gap-4 pb-5 border-b border-white/10 last:border-0 last:pb-0">
                                        <span className="font-display text-2xl font-black text-[#FFC72C] leading-none w-8 shrink-0">{r.tier}</span>
                                        <div>
                                            <p className="font-display text-lg font-bold leading-tight">{r.name}</p>
                                            <p className="text-xs uppercase tracking-widest text-white/60 mt-1">{r.note}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* LOCATION */}
            <section className="bg-white" data-testid="location-section">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="aspect-[4/3] lg:aspect-auto lg:min-h-[520px]">
                        <iframe title="Church Location" src={CHURCH.mapEmbedUrl} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                    </div>
                    <div className="p-10 lg:p-20 flex flex-col justify-center bg-[#2E1A47] text-white">
                        <p className="overline text-[#FFC72C] mb-4">Find Us</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-10">
                            Come worship with us.
                        </h2>
                        <div className="space-y-6 text-base">
                            <div className="flex gap-4 items-start">
                                <MapPin size={22} weight="duotone" className="text-[#FFC72C] shrink-0 mt-1" />
                                <div>
                                    <p className="font-bold mb-1">Address</p>
                                    <p className="text-white/80">{CHURCH.address}</p>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start">
                                <PhoneCall size={22} weight="duotone" className="text-[#FFC72C] shrink-0 mt-1" />
                                <div>
                                    <p className="font-bold mb-1">Phone</p>
                                    <a href={CHURCH.phoneHref} className="text-white/80 hover:text-[#FFC72C]" data-testid="location-phone">{CHURCH.phone}</a>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start">
                                <CalendarBlank size={22} weight="duotone" className="text-[#FFC72C] shrink-0 mt-1" />
                                <div>
                                    <p className="font-bold mb-1">Sunday Worship</p>
                                    <p className="text-white/80">11:00 AM every Sunday</p>
                                </div>
                            </div>
                        </div>
                        <Link to="/contact" data-testid="location-contact-btn" className="mt-10 inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors w-fit">
                            Contact Us <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FLOATING OLIVE POCKET */}
            <OlivePocket />
        </div>
    );
}
